import { baseUrl } from '../constant/constant';
import { getDeviceFingerprint } from './deviceIntelligence';

/**
 * Layer 3 — Behavioral Analytics (client side).
 *
 * Captures privacy-safe behavioural telemetry during checkout and batches it to
 * the server, which turns it into behavioural risk signals (rapid/bot-like
 * checkout, no mouse activity, excessive restarts, coupon testing).
 *
 * PRIVACY: we record only aggregate COUNTS and TIMINGS — how many mouse moves,
 * how many keystrokes, the average interval between keystrokes. We NEVER record
 * which keys were pressed or any field content.
 *
 * Best-effort throughout: any failure is swallowed so telemetry can never break
 * the checkout UX.
 */

const FLUSH_MS = 5000;

let state = null;
let tokenGetter = () => '';

/** Injected by session.js to avoid an import cycle. */
export function setTelemetryTokenGetter(fn) {
    if (typeof fn === 'function') tokenGetter = fn;
}

function now() {
    return (typeof performance !== 'undefined' && performance.now)
        ? performance.now()
        : Date.now();
}

function fresh() {
    return {
        startTime: now(),
        mouseMoves: 0,
        keyEvents: 0,
        lastKeyTs: 0,
        keyIntervalSum: 0,
        keyIntervalCount: 0,
        queue: [],
        timer: null,
        listeners: null,
    };
}

function onMouseMove() {
    if (state) state.mouseMoves++;
}

function onKeyDown() {
    if (!state) return;
    state.keyEvents++;
    const t = now();
    if (state.lastKeyTs) {
        state.keyIntervalSum += (t - state.lastKeyTs);
        state.keyIntervalCount++;
    }
    state.lastKeyTs = t;
}

function elapsedMs() {
    return state ? Math.round(now() - state.startTime) : 0;
}

/** Enqueue an explicit behavioural event (e.g. coupon_attempt, checkout_restart). */
export function recordBehaviorEvent(type, metrics = {}) {
    if (!state) return;
    state.queue.push({ type, metrics, elapsed_ms: elapsedMs() });
}

function drainAggregate() {
    if (!state) return [];
    const events = state.queue;
    state.queue = [];

    // Roll up the movement/keystroke cadence since the LAST flush. Only emit an
    // activity row when something actually happened this interval — otherwise an
    // idle checkout tab would write a row on every flush and bloat the table.
    // (This is what lets the events.length === 0 guard in flush() take effect.)
    if (state.mouseMoves > 0 || state.keyEvents > 0) {
        const avgKeyInterval = state.keyIntervalCount > 0
            ? Math.round(state.keyIntervalSum / state.keyIntervalCount)
            : null;

        events.push({
            type: 'activity',
            metrics: {
                mouse_moves: state.mouseMoves,
                key_events: state.keyEvents,
                avg_key_interval_ms: avgKeyInterval,
            },
            elapsed_ms: elapsedMs(),
        });
    }

    // Reset per-interval counters so each activity row reflects only its own
    // window (not cumulative session totals). lastKeyTs is kept so cadence still
    // measures across the flush boundary.
    state.mouseMoves = 0;
    state.keyEvents = 0;
    state.keyIntervalSum = 0;
    state.keyIntervalCount = 0;

    return events;
}

async function flush(final = false) {
    if (!state) return;
    const token = tokenGetter();
    const events = drainAggregate();
    if (!token || events.length === 0) return;

    try {
        const body = JSON.stringify({
            session_token: token,
            device_fingerprint: getDeviceFingerprint(),
            events,
        });

        // Prefer sendBeacon on the final flush (page may be unloading).
        if (final && typeof navigator !== 'undefined' && navigator.sendBeacon) {
            navigator.sendBeacon(`${baseUrl}/fraud/telemetry`, new Blob([body], { type: 'application/json' }));
            return;
        }

        await fetch(`${baseUrl}/fraud/telemetry`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: final,
        });
    } catch (_) {
        /* telemetry is best-effort */
    }
}

/** Begin capturing behavioural telemetry for the current checkout session. */
export function startBehavioralCapture() {
    if (typeof window === 'undefined') return;
    // If already running, treat a fresh start as a checkout restart.
    if (state) {
        recordBehaviorEvent('checkout_restart');
        return;
    }

    state = fresh();

    const listeners = {
        mousemove: onMouseMove,
        keydown: onKeyDown,
    };
    try {
        window.addEventListener('mousemove', listeners.mousemove, { passive: true });
        window.addEventListener('keydown', listeners.keydown, { passive: true });
    } catch (_) { /* ignore */ }
    state.listeners = listeners;

    recordBehaviorEvent('checkout_start');
    state.timer = setInterval(() => flush(false), FLUSH_MS);
}

/** Stop capturing and flush a final batch. */
export function stopBehavioralCapture(reason = 'abandon') {
    if (!state) return;

    recordBehaviorEvent(reason);

    try {
        if (state.listeners) {
            window.removeEventListener('mousemove', state.listeners.mousemove);
            window.removeEventListener('keydown', state.listeners.keydown);
        }
        if (state.timer) clearInterval(state.timer);
    } catch (_) { /* ignore */ }

    flush(true);
    state = null;
}
